<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {useFilterStore} from '@/store/filter'
import ResetFiltersButton from '@/components/ResetFiltersButton.vue'
import {getBreakpoint} from "@/utils/breakpoint";

const filterStore = useFilterStore()
const currentBp = ref(0)

// ensure selected filters have label meta
function keysNeedingLabels() {
  const out = []
  const sel = filterStore.selected || {}
  for (const k of Object.keys(sel)) {
    const v = sel[k]
    const hasSelection = v === true || (Array.isArray(v) && v.length > 0)
    if (!hasSelection) continue

    const meta = filterStore.filterOptions[k]
    // Booleans/ranges don't need option labels
    if (meta?.type === 'BOOLEAN' || meta?.type === 'NUMBER' || meta?.type === 'PRICE') continue

    // If we already have optionValues cached, skip
    const all = (filterStore.allOptionValues?.[k] || meta?.optionValues || [])
    if (all.length) continue

    out.push(k)
  }
  return out
}

/** “Labels settled” controller: show after we attempted to load labels */
const labelsSettled = ref(false)
let runId = 0
let settleTimer = null

async function settleLabelsForCurrentSelection() {
  // New run id to prevent races
  const myRun = ++runId
  labelsSettled.value = false

  const keys = keysNeedingLabels()
  if (keys.length) {
    await Promise.allSettled(keys.map(k => filterStore.fetchOptions(k)))
  }

  // Even if nothing to load, we consider it “attempted”
  if (myRun === runId) {
    // tiny delay to avoid flicker in fast transitions
    if (settleTimer) clearTimeout(settleTimer)
    settleTimer = setTimeout(() => {
      labelsSettled.value = true
    }, 50)
  }
}

/** Trigger settling after the store finishes its initial load */
watch(
  () => filterStore.loaded,
  v => {
    if (v) settleLabelsForCurrentSelection()
  },
  {immediate: true}
)

/** Re-settle whenever the selection changes */
watch(
  () => filterStore.selected,
  () => {
    if (filterStore.loaded) settleLabelsForCurrentSelection()
  },
  {deep: true}
)

const selectedFilters = computed(() => filterStore.selectedFilters)

// Any active selection (true or non-empty array)
const hasAnySelection = computed(() =>
  Object.values(filterStore.selected || {}).some(v => v === true || (Array.isArray(v) && v.length > 0))
)

// prefer to wait until store says labels are parsed, but show after “settled” anyway
const preferReady = computed(() => filterStore.loaded && filterStore.selectedLabelsReady)

// Final gate: show when either fully ready, or our settling attempt finished — but only if something is selected
const show = computed(() => hasAnySelection.value && (preferReady.value || labelsSettled.value))

const pillRefs = ref([])

function adjustWidths() {
  pillRefs.value.forEach(el => {
    if (!el) return
    const badge = el.querySelector('.category-container')
    const content = el.querySelector('.option-container')
    const padL = parseFloat(getComputedStyle(el).paddingLeft) || 0
    const minW = Math.max((badge?.offsetWidth || 0) + padL, content?.offsetWidth || 0)
    el.style.minWidth = minW + 'px'
    if ((badge?.offsetWidth || 0) + padL > (content?.offsetWidth || 0)) el.classList.add('-badge-width')
    else el.classList.remove('-badge-width')
  })
}

watch([selectedFilters, preferReady, labelsSettled], async () => {
  if (show.value) {
    await nextTick()
    adjustWidths()
  }
})

let resizeRaf = null

function handleResize() {
  if (resizeRaf) return

  resizeRaf = requestAnimationFrame(() => {
    resizeRaf = null
    currentBp.value = getBreakpoint()
  })
}

onMounted(() => {
  window.addEventListener('resize', handleResize, {passive: true})
  // run once to sync initial state with current breakpoint
  handleResize()
})

onBeforeUnmount(() => {
  if (settleTimer) clearTimeout(settleTimer)
})
</script>

<template>
  <div class="product-list--selected-filters" v-if="show">
    <div class="selected-filters-wrapper">
      <div
        v-for="f in selectedFilters"
        :key="`${f.key}-${f.label}`"
        ref="pillRefs"
        class="selected-filter"
      >
        <div class="category-container">
          <span class="category">{{ f.desc }}</span>
        </div>
        <div class="option-container">
          <span class="option">{{ f.label }}</span>
          <button
            type="button"
            class="remove"
            @click="filterStore.removeFilter(f.key, f.value)"
            aria-label="Remove filter"
          >
            <span class="icon-close-before"></span>
          </button>
        </div>
      </div>
    </div>

    <ResetFiltersButton v-if="currentBp >= 2" :count="selectedFilters.length"/>
  </div>
</template>
